<template>
  <section class="my-8 mx-4 md:mx-28 bg-black p-6 md:p-10">
    <div class="mb-8">
      <h1 class="text-[22px] text-gray-400 border-y-2 py-4 text-left font-bold">
        Privacy Policy
      </h1>
      <p class="text-gray-500 text-xs mt-3">Last updated: 11 August 2026</p>
    </div>

    <div class="flex flex-col text-gray-400 gap-6 text-sm text-left">
      <p>
        This policy explains what information
        <span class="text-[#FF0000]">Rally Navigator, LLC</span> collects when
        you use our website, desktop application and mobile roadbook reader,
        and what we do with it. Rally Navigator, LLC is a limited liability
        company registered in the State of Nevada, United States, and is the
        data controller for that information. If
        you have any question about this policy, email
        <a class="text-[#FF0000]" href="mailto:mike@rallynavigator.com"
          >mike@rallynavigator.com</a
        >.
      </p>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">
          Information We Collect
        </h2>
        <p class="mb-2">
          <strong>Account information.</strong> If you create an account, we
          collect your email address and a securely stored password. If you
          subscribe to a paid plan, we keep a record of that subscription and
          its status.
        </p>
        <p class="mb-2">
          <strong>Payment information.</strong> Payments are handled for us by
          third-party payment processors, currently Stripe and PayPal. They
          receive your payment details directly and handle them under their own
          privacy policies. We never see or store your card number, expiry date
          or security code.
        </p>
        <p class="mb-2">
          <strong>Usage information.</strong> When you visit the website we
          collect your IP address, browser and device type, the pages you view,
          how long you spend on them, and the site you arrived from.
        </p>
        <p>
          <strong>Messages you send us.</strong> If you email us for support, we
          keep that correspondence so we can answer you and refer back to it.
        </p>
      </div>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">
          Cookies and Analytics
        </h2>
        <p>
          We use Google Analytics to understand how the site is used and which
          pages are useful. It sets cookies in your browser and collects the
          usage information described above. We use this only in aggregate, to
          improve the site. We do not use advertising cookies, we do not run ads,
          and we do not sell your information to anyone. You can prevent Google
          Analytics from collecting your data by installing Google's
          <a
            class="text-[#FF0000]"
            href="https://tools.google.com/dlpage/gaoptout"
            rel="noopener"
            target="_blank"
            >browser opt-out add-on</a
          >, or by blocking cookies in your browser settings.
        </p>
      </div>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">
          How We Use Your Information
        </h2>
        <p>
          We use it to provide and maintain your account and subscription, to
          answer your questions, to keep the software and website working and
          secure, and to understand how the site is used so we can improve it.
          If you have opted in, we may also email you about new features and
          releases. We do not make automated decisions about you.
        </p>
      </div>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">
          Who We Share It With
        </h2>
        <p>
          We do not sell your information. We share it only with service
          providers who help us operate: hosting and infrastructure providers,
          our payment processors, analytics providers, and email delivery
          services. They may only use it to provide those services to us. We may
          also disclose information where we are legally required to do so.
        </p>
      </div>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">
          Where Your Information Is Held
        </h2>
        <p>
          We are based in the United States, and some of our service providers
          are located there or elsewhere outside your country. This means your
          information may be transferred outside the European Economic Area,
          under the safeguards those providers have in place.
        </p>
      </div>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">
          How Long We Keep It
        </h2>
        <p>
          We keep account and subscription information for as long as your
          account is open, and for a reasonable period afterwards to handle
          billing, support and legal obligations. Analytics data is retained
          according to our analytics provider's standard retention settings. If
          you ask us to delete your account, we will do so.
        </p>
      </div>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">Your Rights</h2>
        <p>
          You can ask us to give you a copy of the information we hold about
          you, correct it if it is wrong, or delete it. You can also ask us to
          stop using it, or object to how we are using it. To do any of these,
          email
          <a class="text-[#FF0000]" href="mailto:mike@rallynavigator.com"
            >mike@rallynavigator.com</a
          >
          and we will respond within one month. If you are in the UK or European
          Economic Area and you are not satisfied with our response, you have the
          right to complain to your local data protection authority.
        </p>
      </div>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">Marketing Emails</h2>
        <p>
          If we send you marketing emails, every one of them has an unsubscribe
          link. You can also email
          <a class="text-[#FF0000]" href="mailto:mike@rallynavigator.com"
            >mike@rallynavigator.com</a
          >
          and we will remove you.
        </p>
      </div>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">Children</h2>
        <p>
          Rally Navigator is not intended for children under 16, and we do not
          knowingly collect information from them. If you believe a child has
          given us their information, email us and we will delete it.
        </p>
      </div>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">
          Other Websites
        </h2>
        <p>
          Our site links to websites we do not control, and our pages may embed
          videos hosted by third parties. This policy does not cover those
          sites. Please read their own privacy policies.
        </p>
      </div>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">
          Changes to This Policy
        </h2>
        <p>
          If we change this policy we will post the updated version here and
          change the date at the top of this page.
        </p>
      </div>
    </div>
  </section>
  <PartnersSmall />
  <RallyLogoDownload />
</template>

<script>
import PartnersSmall from './PartnersSmall.vue';
import RallyLogoDownload from './RallyLogoDownload.vue';

export default {
  name: 'PrivacyPolicy',
  components: {
    PartnersSmall,
    RallyLogoDownload,
  },
};
</script>

<style scoped></style>
